import React, { useMemo } from 'react';
import type { TimeCellData } from 'pos-client/components/customScheduler/types/schedulerTypes';

/**
 * TimePanel Component
 *
 * Displays the time axis of the scheduler with hour and half-hour markers.
 * Supports configurable start/end hours, custom time formatting, and timezone support.
 *
 * Features:
 * - Configurable start and end hours
 * - Hour and half-hour time markers
 * - Custom time cell template rendering
 * - Timezone-aware time formatting
 * - Responsive cell height based on cell duration
 */

export interface TimePanelProps {
  startDayHour: number;
  endDayHour: number;
  cellDuration: number; // in minutes
  cellHeightPx: number;
  timeZone: string;
  timeCellTemplate?: (data: TimeCellData) => React.ReactNode;
  currentDate: Date;
  currentTimePosition?: number | null;
  currentTimeColor?: string;
}

const TimePanel = React.forwardRef<HTMLDivElement, TimePanelProps>(
  (
    {
      startDayHour,
      endDayHour,
      cellDuration,
      cellHeightPx,
      timeZone,
      timeCellTemplate,
      currentDate,
      currentTimePosition = null,
      currentTimeColor = '#52c41a',
    },
    ref
  ) => {
    // Generate time slots for the day
    const timeSlots = useMemo(() => {
      const slots: Date[] = [];
      const baseDate = new Date(currentDate);
      baseDate.setHours(startDayHour, 0, 0, 0);

      const totalHours = endDayHour - startDayHour;
      const slotsPerHour = 60 / cellDuration;
      const totalSlots = totalHours * slotsPerHour;

      for (let i = 0; i < totalSlots; i++) {
        const slotDate = new Date(baseDate);
        slotDate.setMinutes(slotDate.getMinutes() + i * cellDuration);
        slots.push(slotDate);
      }

      return slots;
    }, [currentDate, startDayHour, endDayHour, cellDuration]);

    // Format time for display
    const formatTime = (date: Date): string => {
      try {
        // Use timezone-aware formatting
        const formatter = new Intl.DateTimeFormat('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone,
        });
        return formatter.format(date);
      } catch {
        // Fallback to UTC if timezone is invalid
        return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
      }
    };

    return (
      <div
        ref={ref}
        className="scheduler-time-column"
        style={{
          minWidth: '80px',
          backgroundColor: '#fafafa',
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}
      >

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible',
            position: 'relative',
          }}
        >
          {(() => {
            const endLabelDate = new Date(currentDate);
            endLabelDate.setHours(endDayHour, 0, 0, 0);
            const totalSlots = timeSlots.length;
            const endLabelTop = totalSlots * cellHeightPx;
            const endLabelContent = timeCellTemplate
              ? timeCellTemplate({ date: endLabelDate })
              : formatTime(endLabelDate);
            return (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: `${endLabelTop}px`,
                  left: 0,
                  right: 0,
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: '#666',
                  paddingRight: '8px',
                  lineHeight: '22px',
                  pointerEvents: 'none',
                  zIndex: 20,
                }}
              >
                {endLabelContent}
              </div>
            );
          })()}
          {timeSlots.map((slot, index) => {
            const minutes = slot.getMinutes();
            const isHourMark = minutes === 0;

            return (
              <div
                key={`time-${index}`}
                className="scheduler-time-cell"
                data-hour-mark={isHourMark ? 'true' : undefined}
                style={{
                  height: `${cellHeightPx}px`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: '#666',
                  paddingRight: '8px',
                  textAlign: 'right',
                  backgroundColor: '#fafafa',
                  minHeight: cellHeightPx,
                  lineHeight: '22px',
                  position: 'relative',
                  backgroundImage: isHourMark ? undefined : 'none',
                  ...(index === 0 && { overflow: 'visible' }),
                }}
              >
                {isHourMark && timeCellTemplate ? (
                  <div
                    style={
                      index === 0
                        ? {
                            position: 'absolute',
                            top: 0,
                            lineHeight: 1,
                            zIndex: 20,
                          }
                        : { transform: 'translateY(-50%)' }
                    }
                  >
                    {timeCellTemplate({ date: slot })}
                  </div>
                ) : isHourMark ? (
                  <span
                    style={
                      index === 0
                        ? {
                            position: 'absolute',
                            top: 0,
                            lineHeight: 1,
                            zIndex: 20,
                          }
                        : { transform: 'translateY(-50%)' }
                    }
                  >
                    {formatTime(slot)}
                  </span>
                ) : null}
              </div>
            );
          })}

          {currentTimePosition !== null && currentTimePosition >= 0 && (
            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: `${currentTimePosition}px`,
                right: 0,
                transform: 'translateY(-50%)',
                backgroundColor: '#f6ffed',
                color: currentTimeColor,
                border: `2px solid ${currentTimeColor}`,
                padding: '1px 8px',
                borderRadius: 10,
                fontSize: 11,
                lineHeight: 1.2,
                fontWeight: 600,
                pointerEvents: 'none',
                zIndex: 25,
                whiteSpace: 'nowrap',
                boxShadow: '0 0 4px rgba(82, 196, 26, 0.35)',
              }}
            >
              {formatTime(new Date())}
            </div>
          )}
        </div>
      </div>
    );
  }
);

TimePanel.displayName = 'TimePanel';

export default TimePanel;
